import React, { useState, useRef, useEffect } from 'react';

interface CustomSelectProps {
  id: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  hasError?: boolean;
  onBlur?: () => void;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  value,
  options,
  onChange,
  placeholder = 'Select an option',
  className = '',
  disabled = false,
  ariaLabel,
  hasError = false,
  onBlur
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (onBlur) onBlur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        if (onBlur) onBlur();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const currentIndex = options.indexOf(value);
        if (e.key === 'ArrowDown') {
          const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
          onChange(options[nextIndex]);
        } else {
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          onChange(options[prevIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, options, value, onChange, onBlur]);

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Handle option selection
  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  // Determine displayed value
  const displayValue = value || placeholder;

  return (
    <div 
      ref={selectRef} 
      className={`relative ${className}`}
      aria-labelledby={id}
    >
      <button
        type="button"
        id={id}
        className={`flex items-center justify-between w-full bg-black/50 border ${hasError ? 'border-red-500' : 'border-gray-800'} rounded-lg px-3 py-2.5 sm:py-3 text-white 
        placeholder-gray-400 focus:outline-none focus:ring-2 ${hasError ? 'focus:ring-red-500' : 'focus:ring-purple-500'} focus:border-transparent 
        text-sm sm:text-base ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-gray-700'}`}
        onClick={toggleDropdown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
      >
        <span className={`${!value ? 'text-gray-400' : 'text-white'}`}>
          {displayValue}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div 
          className="absolute z-40 w-full mt-1 bg-black border border-gray-800 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          role="listbox"
        >
          {options.length === 0 ? (
            <div className="py-2 px-3 text-gray-400 text-sm">No options available</div>
          ) : (
            options.map((option) => (
              <button
                key={option}
                type="button"
                className={`w-full text-left px-3 py-2.5 hover:bg-gray-800 text-sm sm:text-base ${
                  option === value ? 'bg-gray-800 text-white' : 'text-gray-300'
                }`}
                onClick={() => handleSelect(option)}
                role="option"
                aria-selected={option === value}
              >
                {option}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};